/* eslint-disable react/require-default-props */
import Checkbox from '@mui/material/Checkbox'
import FormControlLabel from '@mui/material/FormControlLabel'
import { Theme } from '@mui/material/styles'
import { FormControl, FormHelperText } from '@mui/material'
import { useStores } from 'stores/utils/hooks/useStores'
import { observer } from 'mobx-react'
import { ReactNode, ChangeEvent, useCallback, useMemo, memo } from 'react'
import useEmotionStyles from 'hooks/useEmotionStyles'
import FieldDataCollector from '../FieldDataCollector'
import { PathFormFieldProps } from '../../interfaces/PathFormComponents.interface'
import WithFieldInteraction from '../../utils/tracking/amplitude/WithFieldInteraction'
import { usePatchedField } from '../../stores/utils/hooks/usePatchedField'
import WithIncreasedEmphasis from './WithIncreasedEmphasis'

type Props = Omit<
  PathFormFieldProps<boolean>,
  'defaultValue' | 'tooltip' | 'onTooltip' | 'showTooltip'
> & {
  type?: boolean
  disabled?: boolean
  label?: ReactNode
  increaseEmphasis?: boolean
  alignOnTop?: boolean
}

interface StyleProps {
  hasError: boolean
  increaseEmphasis: boolean
  isChecked: boolean
  alignOnTop: boolean
}

const getColor = (
  theme: Theme,
  { hasError, isChecked }: Partial<StyleProps>
) => {
  if (isChecked) {
    return theme.palette.primary.main
  }

  if (hasError) {
    return theme.palette.error.main
  }

  return theme.palette.grey[700]
}

const styles = (
  theme: Theme,
  { hasError, increaseEmphasis, isChecked, alignOnTop }: StyleProps
) => ({
  checkbox: {
    '& svg': {
      color: getColor(theme, { hasError, isChecked })
    }
  },
  formLabel: {
    '& > span': {
      fontSize: increaseEmphasis
        ? theme.typography.body1.fontSize
        : '0.8125rem',
      paddingTop: alignOnTop ? 0 : 'inherit',
      alignSelf: alignOnTop ? 'flex-start' : 'unset'
    }
  }
})

const YesNoCheckbox = observer(
  ({ path, label, increaseEmphasis = false, alignOnTop, disabled }: Props) => {
    const { input, error } = usePatchedField(path, {
      type: 'checkbox'
    })

    const {
      traversal: { removeErrorsForPath }
    } = useStores()

    const handleChange = useCallback(
      (e: ChangeEvent<HTMLInputElement>) => {
        input.onChange(e)
        input.onBlur()
        removeErrorsForPath(path)
      },
      [input, removeErrorsForPath, path]
    )

    const isEmphasized = useMemo(
      () => increaseEmphasis && !input.checked,
      [increaseEmphasis, input.checked]
    )

    const hasError = !!error
    const { checkbox, formLabel } = useEmotionStyles<
      ReturnType<typeof styles>,
      StyleProps
    >(styles, {
      hasError,
      increaseEmphasis,
      isChecked: !!input.checked,
      alignOnTop: alignOnTop || false
    })

    return (
      <FormControl error={hasError}>
        <FormControlLabel
          className={formLabel}
          control={
            <WithIncreasedEmphasis display={isEmphasized}>
              <WithFieldInteraction
                trackFocusEvents={false}
                trackOnChange
                path={path}
              >
                <Checkbox
                  className={checkbox}
                  checked={input.checked}
                  name={input.name}
                  onBlur={input.onBlur}
                  onFocus={input.onFocus}
                  value={input.value}
                  disabled={disabled}
                  onChange={handleChange}
                  color="primary"
                />
              </WithFieldInteraction>
            </WithIncreasedEmphasis>
          }
          label={label}
        />
        {error && <FormHelperText>{error}</FormHelperText>}
      </FormControl>
    )
  }
)

const PathCheckboxYesNo = (props: Props) => {
  const {
    path,
    labelPath,
    label,
    increaseEmphasis,
    alignOnTop,
    type,
    disabled
  } = props

  return (
    <FieldDataCollector path={path} labelPath={labelPath}>
      <YesNoCheckbox
        path={path}
        labelPath={labelPath}
        label={label}
        increaseEmphasis={increaseEmphasis}
        alignOnTop={alignOnTop}
        type={type}
        disabled={disabled}
      />
    </FieldDataCollector>
  )
}

export default memo(PathCheckboxYesNo)
